import { Controller } from '@hotwired/stimulus';
import { getCableConsumer } from '../channels/consumer';

export default class extends Controller {
  static targets = ['messages', 'input', 'form'];
  static values = {
    conversationId: Number,
    realtimeMode: { type: String, default: 'cable' },
    pollUrl: String,
    pollInterval: { type: Number, default: 10000 },
    wizardAvatarUrl: String,
    userAvatarUrl: String
  };

  connect() {
    this.pollTimer = null;
    this.pollingActive = false;
    this.boundVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);

    if (this.realtimeModeValue === 'poll') {
      this.startPolling();
    } else {
      this.subscription = getCableConsumer().subscriptions.create(
        { channel: 'Wizard::AdminChannel' },
        {
          received: (data) => this.onCableMessage(data)
        }
      );
    }
    this.scrollMessages();
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    this.subscription?.unsubscribe();
    this.stopPolling();
  }

  startPolling() {
    this.pollingActive = true;
    this.clearPollTimer();
    this.pollMessages();
    this.schedulePoll();
  }

  stopPolling() {
    this.pollingActive = false;
    this.clearPollTimer();
  }

  clearPollTimer() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  schedulePoll() {
    if (document.hidden) return;

    this.pollTimer = setTimeout(() => this.pollMessages(), this.pollIntervalValue);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.clearPollTimer();
      return;
    }

    if (this.pollingActive && this.realtimeModeValue === 'poll') {
      this.pollMessages();
    }
  }

  async pollMessages() {
    try {
      const url = new URL(this.pollUrlValue, window.location.origin);
      const lastId = this.latestMessageId();
      if (lastId) url.searchParams.set('since_message_id', String(lastId));

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) return;

      const data = await response.json();
      const wasNearBottom = this.isNearBottom();
      const before = this.messagesTarget.childElementCount;
      (data.messages || []).forEach((message) => {
        if (message.sender_role !== 'user') return;
        this.appendMessage({ ...message, mine: false });
      });
      if (wasNearBottom && this.messagesTarget.childElementCount > before) this.scrollMessages();
    } catch {
      // ignore
    } finally {
      if (this.pollingActive) this.schedulePoll();
    }
  }

  latestMessageId() {
    const nodes = this.messagesTarget.querySelectorAll('[data-message-id]');
    if (!nodes.length) return null;

    return Math.max(...[...nodes].map((node) => Number(node.dataset.messageId)));
  }

  handleEnter(event) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

    event.preventDefault();

    if (typeof this.formTarget.requestSubmit === 'function') {
      this.formTarget.requestSubmit();
    } else {
      this.formTarget.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  async send(event) {
    event.preventDefault();
    const body = this.inputTarget.value.trim();
    if (!body) return;

    const formData = new FormData(this.formTarget);
    const response = await fetch(this.formTarget.action, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken()
      },
      body: formData
    });

    if (!response.ok) {
      window.alert('Could not send reply.');
      return;
    }

    const message = await response.json();
    this.appendMessage(message);
    this.inputTarget.value = '';
    this.scrollMessages();
  }

  onCableMessage(data) {
    if (data.type !== 'message') return;
    if (data.conversation_id !== this.conversationIdValue) return;
    if (data.message?.sender_role !== 'user') return;

    const wasNearBottom = this.isNearBottom();
    this.appendMessage({ ...data.message, mine: false });
    if (wasNearBottom) this.scrollMessages();
  }

  appendMessage(message) {
    if (this.messagesTarget.querySelector(`[data-message-id="${message.id}"]`)) return;

    this.messagesTarget.appendChild(this.buildMessageElement(message));
  }

  buildMessageElement(message) {
    const mine = message.mine ?? message.sender_role === 'admin';
    const fromWizard = message.sender_role === 'admin';

    const row = document.createElement('div');
    row.dataset.messageId = String(message.id);
    row.className = `wizard-chat__message wizard-chat__message--${mine ? 'mine' : 'theirs'}`;

    const avatarWrap = document.createElement('div');
    avatarWrap.className = 'wizard-chat__avatar';
    avatarWrap.setAttribute('aria-hidden', 'true');

    const avatarImg = document.createElement('img');
    avatarImg.className = `wizard-chat__avatar-img${fromWizard ? ' wizard-chat__avatar-img--wizard' : ''}`;
    avatarImg.src = fromWizard ? this.wizardAvatarUrlValue : this.userAvatarUrlValue;
    avatarImg.alt = fromWizard ? 'The Wizard' : '';
    avatarImg.loading = 'lazy';
    avatarWrap.appendChild(avatarImg);

    const bubble = document.createElement('div');
    bubble.className = `wizard-chat__bubble wizard-chat__bubble--${mine ? 'mine' : 'theirs'}`;

    const body = document.createElement('p');
    body.className = 'wizard-chat__body whitespace-pre-wrap';
    body.textContent = message.body;

    const time = document.createElement('time');
    time.className = 'wizard-chat__time';
    time.dateTime = message.created_at;
    time.textContent = new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    bubble.append(body, time);
    row.append(avatarWrap, bubble);

    return row;
  }

  scrollMessages() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  isNearBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
